import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Alert, Button } from "react-bootstrap";
import { useParams } from "react-router-dom";
import Loading from "../Loading";
import type { HouseholdWithAccounts } from "../../../api/src/households/households.service";
import type { RebalanceWithMetrics } from "../../../api/src/rebalances/rebalances.service";
import RebalanceHouseholdExplainV3 from "../rebalances/explain/RebalanceHouseholdExplain";
import type { ExplainResponseV3 } from "../rebalances/explain/lib/explainTypes";
import {
  RebalanceContext,
  type RebalanceCheckData,
} from "../rebalances/RebalanceInfo";
import AdminRebalanceExplainInput, {
  parseRebalanceId,
} from "./AdminRebalanceExplainInput";
import { useAdminRebalanceAudit } from "./useAdminRebalanceAudit";
import {
  useAdminRebalanceExplain,
  type AdminRebalanceExplainBundle,
} from "./useAdminRebalanceExplain";

// Owning-firm header label. The literal `Cross-firm admin view` distinguishes
// this read-only viewer from the firm-scoped Explain page (R13).
export const OWNING_FIRM_PREFIX = "Cross-firm admin view";
export const FIRM_UNKNOWN_LABEL = "firm unknown";

/**
 * Resolve the firm label for the header band (R4 / R5 / R13). Never blank:
 * falls back to the firm id, then to the literal `firm unknown`.
 */
export function resolveFirmLabel(
  bundle: Pick<AdminRebalanceExplainBundle, "firmName" | "firmId">,
): string {
  const name = bundle.firmName?.trim();
  if (name) return name;
  if (bundle.firmId != null && Number.isFinite(bundle.firmId)) {
    return String(bundle.firmId);
  }
  return FIRM_UNKNOWN_LABEL;
}

/**
 * Read-only, cross-firm v3 Explain viewer
 * (s-read-only-admin-explain-019ea1c7). Mounted at
 * `/admin/rebalances/:rebalanceId/explain` behind the Spec-1 `StaffRoute`.
 *
 * Reuses the SINGLE v3 Explain tree (no fork): renders
 * `RebalanceHouseholdExplainV3` with the bundle's `explain` payload passed via
 * its `response` prop (skips the firm-scoped `useExplain` fetch), inside a
 * `RebalanceContext` with `readOnly: true` so any mutating control suppresses
 * itself. The bundle's check is given to the tree through
 * `RebalanceContext.givenCheck`, so `ChecksPanel` shows it and sends no
 * `/check` read (R23); the tree issues no firm-scoped or mutating request
 * (R2 / R3 / R8 / AC3).
 */
export default function AdminRebalanceExplain() {
  const { rebalanceId: rebalanceIdStr } = useParams();
  const rebalanceId =
    rebalanceIdStr == null ? null : parseRebalanceId(rebalanceIdStr);

  // A segment that is not a positive whole number: present the rebalance-id
  // input (R14 / R15).
  if (rebalanceId == null) {
    return <AdminRebalanceExplainInput />;
  }

  return <AdminRebalanceExplainContent rebalanceId={rebalanceId} />;
}

function AdminRebalanceExplainContent({
  rebalanceId,
}: {
  rebalanceId: number;
}) {
  const queryClient = useQueryClient();
  const { status, bundle, retry } = useAdminRebalanceExplain(rebalanceId);
  // Third primed stream (D8): fetch the cross-firm audit so its findings can be
  // seeded into the firm tree's own `useAudit` cache key BEFORE the tree mounts.
  const { audit, settled: auditSettled } = useAdminRebalanceAudit(rebalanceId);

  // Prime the tree's `useAudit` cache key (`/rebalances/:id/audit`) from the
  // staff audit endpoint so `AuditFindingCards` + the header rollup resolve
  // from cache — the tree fires NO firm-scoped `/audit` GET (D8 / AC5). Once
  // the audit request has settled (success OR failure), seed the cache; a
  // failed audit degrades to `null` (no findings) rather than blocking Explain.
  useEffect(() => {
    if (auditSettled) {
      queryClient.setQueryData([`/rebalances/${rebalanceId}/audit`], {
        data: audit ?? null,
      });
    }
  }, [auditSettled, audit, rebalanceId, queryClient]);

  if (status === "loading") {
    return <Loading message="cross-firm explain" />;
  }

  // 404 — unknown/invalid id. Renders `Rebalance not found` and NONE of the
  // v3 sections (R10 / AC8).
  if (status === "not-found") {
    return (
      <Alert variant="warning" role="alert">
        Rebalance not found
      </Alert>
    );
  }

  // Non-404 failure (5xx / network / timeout). Visible, non-empty error state
  // distinct from not-found (R11 / AC9). "Try again" sends the bundle read
  // again (R32).
  if (status === "error" || !bundle) {
    return (
      <Alert variant="danger" role="alert">
        <p>Failed to load the cross-firm Explain. Please retry.</p>
        <Button variant="outline-danger" size="sm" onClick={retry}>
          Try again
        </Button>
      </Alert>
    );
  }

  const firmLabel = resolveFirmLabel(bundle);

  // Valid rebalance with no v3 Explain data — distinct from not-found
  // (R12 / AC10). Firm header still renders so staff know whose data this is.
  if (bundle.explain == null) {
    return (
      <section aria-label="Cross-firm admin Explain">
        <FirmHeader firmLabel={firmLabel} />
        <Alert variant="info" role="status">
          This rebalance has no v3 Explain data to display.
        </Alert>
      </section>
    );
  }

  // Minimal RebalanceContext for the tree: `rebalance.id` drives the tree's
  // `rebalanceId`; `household.accounts` feeds `useAccountResolver` (name /
  // number join onto the PII-free payload). Cast through `unknown` — the tree
  // reads only this narrow slice cross-firm; the full firm-scoped shapes are
  // not loaded on this read-only path.
  const rebalance = {
    id: bundle.explain.rebalance_id,
    householdId: bundle.household.id,
  } as unknown as RebalanceWithMetrics;

  // PII-free: the bundle carries only the non-PII `label` per account/household
  // (no client name or number). The shared resolver prefers `label` over the
  // advisor `displayName`/`displayNumber`, so the tree renders the redacted
  // token and no number (s-admin-view-pii-minimization-019eb293 R5/R6/AC4/AC5).
  const household = {
    id: bundle.household.id,
    label: bundle.household.label,
    accounts: bundle.household.accounts.map((a) => ({
      id: a.id,
      label: a.label,
      accountBalance: a.accountBalance ?? 0,
    })),
  } as unknown as HouseholdWithAccounts;

  return (
    <section aria-label="Cross-firm admin Explain">
      <FirmHeader firmLabel={firmLabel} />
      <RebalanceContext.Provider
        value={{
          rebalance,
          household,
          readOnly: true,
          givenCheck: {
            check: (bundle.check ?? null) as RebalanceCheckData | null,
          },
        }}
      >
        {/* The api + client `ExplainResponseV3` are intentional mirrors of the
            same python schema (see explainTypes.ts header); cast at the
            boundary to the client mirror the tree consumes. */}
        <RebalanceHouseholdExplainV3
          response={bundle.explain as unknown as ExplainResponseV3}
        />
      </RebalanceContext.Provider>
    </section>
  );
}

function FirmHeader({ firmLabel }: { firmLabel: string }) {
  return (
    <Alert variant="secondary" className="mb-3">
      <strong>
        {OWNING_FIRM_PREFIX} — {firmLabel}
      </strong>
    </Alert>
  );
}
